"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.imageUrltoImageData = exports.getValues = exports.formHasErrors = exports.checkboxHandler = exports.parseOption = exports.updateMandatory = exports.initiateFormFields = exports.processField = void 0;
const validator_1 = __importDefault(require("validator"));
/**
 * Process data from given field and prepare it for a form mutation.
 */
function processField(name, value, mandatory, options = {}, textLabels, customValidationFunction) {
    const { type, min, forceErrorMessage, touched = false } = options;
    // If the value is an array, remove its empty values for safety.
    const valueIsArray = Array.isArray(value);
    const safeValue = valueIsArray
        ? value.filter(item => Number.isInteger(item) || item instanceof Object || item.length)
        : value;
    const valueIsEmpty = !safeValue || (valueIsArray && safeValue.length === 0);
    // Vintage, mutable javascript ;)
    let validation = null;
    let help = null;
    // VALIDATION - If any check will fail, raise error state and set help message.
    if (mandatory && valueIsEmpty) {
        // If the field is mandatory and its value is empty, set an error. Otherwise
        // continue the validation.
        validation = 'error';
        help = textLabels.mandatoryField;
    }
    else if ((safeValue === null || safeValue === void 0 ? void 0 : safeValue.length) && safeValue.length > 0) {
        // Force error message if it is present and abandon further validation.
        if (forceErrorMessage) {
            return {
                [name]: {
                    value: safeValue,
                    touched: true,
                    validation: 'error',
                    mandatory,
                    help: forceErrorMessage,
                },
            };
        }
        switch (type) {
            case 'email':
                if (!validator_1.default.isEmail(value)) {
                    validation = 'error';
                    help = textLabels.emailInvalid;
                }
                break;
            case 'url':
                if (!validator_1.default.isURL(value)) {
                    validation = 'error';
                    help = textLabels.urlInvalid;
                }
                break;
            case 'tel':
                if (!value.match(/^\s*(?:\+?(\d{1,3}))?([-. (]*(\d{3})[-. )]*)?((\d{3})[-. ]*(\d{2,4})(?:[-.x ]*(\d+))?)\s*$/g)) {
                    validation = 'error';
                    help = textLabels.phoneInvalid;
                }
                break;
            case 'postcode':
                if (!validator_1.default.isPostalCode(value, 'DE')) {
                    validation = 'error';
                    help = textLabels.postCodeInvalid;
                }
                break;
            default:
                // Handle custom validation function.
                if (type && customValidationFunction) {
                    const error = customValidationFunction(value, type);
                    if (error) {
                        validation = 'error';
                        help = error;
                        // TODO: Multilanguage support for error message.
                    }
                }
                // Minimal length option support.
                if (min && safeValue.length < min) {
                    validation = 'error';
                    help = textLabels.minChars.replace(':length:', min.toString());
                }
                break;
        }
    }
    // If there is no error, value is not empty, and field state was touched,
    // indicate the success state.
    if (validation !== 'error' && (!valueIsEmpty ||
        (typeof value === 'object' && !Array.isArray(value))) && touched) {
        validation = 'success';
    }
    return {
        [name]: {
            value: safeValue,
            touched,
            validation,
            mandatory,
            help,
            type,
        },
    };
}
exports.processField = processField;
/**
 * Generate initial form state, where all values are undefined.
 */
function initiateFormFields(fieldNames, mandatory) {
    return fieldNames.reduce((acc, field) => (Object.assign(Object.assign({}, acc), { [field]: {
            value: undefined,
            touched: false,
            validation: null,
            mandatory: mandatory && mandatory.includes(field),
            help: null,
        } })), {});
}
exports.initiateFormFields = initiateFormFields;
/**
 * Reset valdiation states of all fields in a form.
 */
function updateMandatory(fieldsData, mandatory) {
    const updatedFieldsData = {};
    Object.keys(fieldsData).forEach(key => {
        const { value, help } = fieldsData[key];
        const isMandatory = mandatory && mandatory.includes(key);
        updatedFieldsData[key] = {
            value,
            touched: false,
            // If the field is not on mandatory anymore, validation must be cleaned up.
            validation: (fieldsData[key].validation === 'error' && !isMandatory) ? null : fieldsData[key].validation,
            mandatory: isMandatory,
            help,
        };
    });
    return updatedFieldsData;
}
exports.updateMandatory = updateMandatory;
/**
 * Parse option, which can be a string or an object, to return an object.
 */
function parseOption(option) {
    const optionLabel = (typeof option === 'string') ? option : option.label;
    const optionValue = (typeof option === 'string') ? option : option.value;
    return { optionLabel, optionValue };
}
exports.parseOption = parseOption;
/**
 * Update single checkbox value in a list of all checkboxes.
 */
function checkboxHandler(checked, newValue, previousValues) {
    if (checked) {
        if (previousValues) {
            return [...previousValues, newValue];
        }
        return [newValue];
    }
    return previousValues.filter(item => item !== newValue);
}
exports.checkboxHandler = checkboxHandler;
/**
 * Check whether whole form is filled correctly.
 */
function formHasErrors(fieldsData) {
    // Check only fields of given keys, otherwise check whole form.
    let mandatoryButEmpty = false;
    let hasAnyError = false;
    Object.keys(fieldsData).forEach(key => {
        const { value, validation, mandatory } = fieldsData[key];
        if (mandatory && (typeof value === 'undefined' ||
            (typeof value === 'string' && value === '') ||
            (Array.isArray(value) && value.length === 0) ||
            (typeof value === 'object' && !Array.isArray(value) && (value === null || !Object.keys(value).length)))) {
            mandatoryButEmpty = true;
        }
        if (validation === 'error') {
            hasAnyError = true;
        }
    });
    return mandatoryButEmpty || hasAnyError;
}
exports.formHasErrors = formHasErrors;
/**
 * Get values from all fields and organize them into API friendly format.
 */
function getValues(fieldsData) {
    const values = {};
    Object.keys(fieldsData).forEach(key => {
        values[key] = fieldsData[key].value;
    });
    return values;
}
exports.getValues = getValues;
/**
 * Convert image url to image data format that is compatible with image upload
 * input.
 */
function imageUrltoImageData(imageUrl) {
    if (imageUrl) {
        return {
            name: imageUrl.split('/').pop(),
            data: imageUrl,
            type: 'image',
        };
    }
    return null;
}
exports.imageUrltoImageData = imageUrltoImageData;
